import React, {
  useEffect, useState, useRef
} from 'react'
import PropTypes from 'prop-types'
import { EventEmitter } from 'events'
import IframeClientContext from './IframeClientContext'
import IframeClient from './IframeClient'
import * as Commands from './commands'
import GetConfigurationResourceError from './errors/GetConfigurationResourceError'
import GetContextResourceError from './errors/GetContextResourceError'
import { logger } from './logger'


const noop = () => {}
const initialActions = {
  launch: noop,
  close: noop,
  getWS: noop,
  closeWS: noop,
  messageWS: noop,
  setDimensions: noop,
  openLink: noop,
  getResource: noop,
  commandTo: noop
}

const IframeClientProvider = ({ children, name, renderBeforeInitialised }) => {
  const [connecting, setConnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [clientConfiguration, setClientConfiguration] = useState(undefined)
  const [hostContext, setHostContext] = useState(undefined)
  const [error, setError] = useState(undefined)

  const [closing, setClosing] = useState(false)
  const [closingType, setClosingType] = useState('')
  const [closed, setClosed] = useState(false)

  const nameRef = useRef(name)
  const clientRef = useRef()
  const dataNotifierRef = useRef()
  const actionsRef = useRef(initialActions)

  const onDataRequest = async ({ payload }) => {
    // NOTE: it is assumed that the host publishing this client will not request any data from it,
    // eg, the launcher does not request data from button, modal, inline
    // In case "requests" needs to be cascaded down, some of the architecture needs changing!
    logger.warn('IframeClientProvider is not handling data request! Received', { payload })
  }

  const onCommandRequest = async ({ payload, correlationId }) => {
    // NOTE: it is assumed that the host publishing this client will only send these commands
    // which are handled by changing some states of this component
    // In case "commands" needs to be cascaded down, some of the architecture needs changing!

    const { name: commandName, options } = payload


    switch (commandName) {
      case Commands.INITIATE_CLOSING: {
        const { closeType } = options
        setClosing(true) // Expecting this state changed to be handled by consumer
        setClosingType(closeType) // Expecting this state changed to be handled by consumer
        clientRef.current.sendCommandResponse({ success: true }, correlationId)
        break
      }

      case Commands.CLOSE: {
        setClosed(true) // Expecting this state changed to be handled by consumer
        clientRef.current.sendCommandResponse({ success: true }, correlationId)
        clientRef.current.disconnect()
        break
      }

      default:
        break
    }
  }

  const onDataTransfer = async ({ payload }) => {
    // NOTE: it is assumed that the host publishing this client at some point transfers data
    // These dataNotifier role is to broadcast these data!
    dataNotifierRef.current.emit('data', payload)
  }


  useEffect(() => {
    const iframeClient = new IframeClient(null, null, nameRef.current)
    clientRef.current = iframeClient
    dataNotifierRef.current = new EventEmitter()

    iframeClient.dataRequestHandler = onDataRequest
    iframeClient.commandRequestHandler = onCommandRequest
    iframeClient.dataTransferHandler = onDataTransfer

    actionsRef.current = {
      launch: iframeClient.sendLaunch.bind(iframeClient),
      close: iframeClient.sendClose.bind(iframeClient),
      getWS: iframeClient.requestWS.bind(iframeClient),
      getWSReadyState: iframeClient.requestWSReadyState.bind(iframeClient),
      closeWS: iframeClient.closeWS.bind(iframeClient),
      messageWS: iframeClient.sendWSMessage.bind(iframeClient),
      setDimensions: iframeClient.sendDimensions.bind(iframeClient),
      openLink: iframeClient.openSimpleLink.bind(iframeClient),
      getResource: iframeClient.getResource.bind(iframeClient),
      commandTo: iframeClient.commandTo.bind(iframeClient),
    }

    setConnecting(true)
    iframeClient.connect()
      .then(() => setConnected(true))
      .catch(e => setError(e))
      .then(() => setConnecting(false))

    return () => {
      // unmount (disconnect/destroy) clientRef if need be
    }
  }, [])

  useEffect(() => {
    if (connected) {
      clientRef.current.getConfiguration()
        .then(config => setClientConfiguration(config))
        .catch(e => setError(new GetConfigurationResourceError(e.message)))
      clientRef.current.getContext()
        .then(config => setHostContext(config))
        .catch(e => setError(new GetContextResourceError(e.message)))
    }
  }, [connected])

  const context = {
    connecting,
    closing,
    closingType,
    connected,
    closed,
    clientConfiguration,
    hostContext,
    actions: actionsRef.current,
    dataNotifier: dataNotifierRef.current,
    error
  }

  const initialised = clientConfiguration && hostContext
  const shouldRender = initialised || renderBeforeInitialised

  return (
    <IframeClientContext.Provider value={context}>
      {shouldRender ? children : null}
    </IframeClientContext.Provider>
  )
}

IframeClientProvider.defaultProps = {
  children: null,
  renderBeforeInitialised: false,
}

IframeClientProvider.propTypes = {
  name: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.element
  ]),
  renderBeforeInitialised: PropTypes.bool,
}

export default IframeClientProvider
